import clsx from "clsx";
import React, { useRef, useState } from "react";
import ReactDOM from "react-dom";
import Slider from "react-slick";
import CloseButton from "web/react/components/buttons/close-button/close-button";
import { MoreProducts } from "web/react/components/product-gallery/product-gallery-more-products/product-gallery-more-products";
import "web/react/components/product-gallery/slick-theme.less";
import "web/react/components/product-gallery/slick.less";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { Text } from "web/react/emo/text";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { useInStockProductPageContext } from "web/react/pages/product/in-stock/in-stock-product-page/in-stock-product-page.context";

import {
    Image,
    ProductGalleryImage,
} from "web/react/pages/product/product-image-gallery/product-gallery-image";
import { getSafariMajorVersion } from "web/react/utils/user-agent";
import { gettext } from "web/script/modules/django-i18n";
import * as styles from "./product-image-gallery-fullscreen.css";
import { ThumbnailStrip } from "./thumbnail-strip/thumbnail-strip";

interface ProductImageGalleryFullscreenProps {
    images: Image[];
    initialSlideIndex?: number;
    onClose: () => void;
}

interface ArrowButtonProps {
    variant: "prev" | "next";
    className?: string;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
    isMobile?: boolean;
    disabled?: boolean;
}

function ArrowButton({
    variant,
    className,
    onClick,
    onKeyDown,
    isMobile = false,
    disabled = false,
}: ArrowButtonProps): React.ReactElement {
    const rotation = variant === "prev" ? 90 : -90;
    const ariaLabel =
        variant === "prev"
            ? gettext("product_image_gallery_fullscreen.previous_image")
            : gettext("product_image_gallery_fullscreen.next_image");
    const baseStyle = isMobile
        ? styles.mobileNavigationArrow
        : variant === "prev"
        ? styles.prevArrow
        : styles.nextArrow;

    return (
        <button
            className={clsx(baseStyle, className)}
            onClick={onClick}
            onKeyDown={onKeyDown}
            aria-label={ariaLabel}
            disabled={disabled}
            tabIndex={0}
        >
            <SVGIcon name="chevron-thin" rotate={rotation} className={styles.arrowIcon} />
        </button>
    );
}

function PrevArrow(props: Omit<ArrowButtonProps, "variant">): React.ReactElement {
    return <ArrowButton {...props} variant="prev" />;
}

function NextArrow(props: Omit<ArrowButtonProps, "variant">): React.ReactElement {
    return <ArrowButton {...props} variant="next" />;
}

export function ProductImageGalleryFullscreen({
    images,
    initialSlideIndex = 0,
    onClose,
}: ProductImageGalleryFullscreenProps): React.ReactElement {
    const isIOS18 = getSafariMajorVersion() === 18;
    const sliderRef = useRef<Slider | null>(null);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(initialSlideIndex);
    const { isMobileViewport } = useDomViewport();
    const { relatedProducts } = useInStockProductPageContext();
    const showMoreProducts = isMobileViewport && relatedProducts && relatedProducts.length > 0;

    const totalSlides = images.length + (showMoreProducts ? 1 : 0);
    const canGoToPrevious = currentSlideIndex > 0;
    const canGoToNext = currentSlideIndex < totalSlides - 1;

    const handlePreviousClick = (): void => {
        if (canGoToPrevious) {
            sliderRef.current?.slickPrev();
        }
    };

    const handleNextClick = (): void => {
        if (canGoToNext) {
            sliderRef.current?.slickNext();
        }
    };
    const currentImagePosition = currentSlideIndex + 1;

    return ReactDOM.createPortal(
        <div data-testid="fullscreen-gallery" className={styles.overlay} tabIndex={-1}>
            <div className={styles.topControls}>
                {images.length > 0 && currentSlideIndex < images.length ? (
                    <div
                        className={styles.imagePositionIndicator}
                        aria-label={gettext(
                            "product_image_gallery.invisible_text_for_accessibility.position_indicator",
                            {
                                current_image_position: currentImagePosition,
                                image_length: images.length,
                            }
                        )}
                    >
                        <Text as="span" textStyle="body-s">
                            {`${currentImagePosition}/${images.length}`}
                        </Text>
                    </div>
                ) : null}
                <span className={styles.closeButton}>
                    <CloseButton onClick={onClose} />
                </span>
            </div>
            <Slider
                ref={sliderRef}
                speed={400}
                slidesToShow={1}
                centerMode={false}
                infinite={false}
                arrows
                swipe
                touchMove
                draggable={false}
                dots={false}
                beforeChange={(_, newIndex) => setCurrentSlideIndex(newIndex)}
                prevArrow={<PrevArrow />}
                nextArrow={<NextArrow />}
                className={styles.slider}
                initialSlide={initialSlideIndex}
            >
                {images.map((image, index) => (
                    <div key={`${image.id}-${index}`} className={styles.imageSlide}>
                        <ProductGalleryImage
                            src={image.url}
                            alt={image.altText ?? undefined}
                            lazy={!isIOS18}
                            isFullscreen
                            blurImageOnLoad={!isIOS18}
                            tabIndex={-1}
                        />
                    </div>
                ))}
                {showMoreProducts && (
                    <MoreProducts
                        key="related-slide"
                        products={relatedProducts.slice(0, 4)}
                        fullscreen
                    />
                )}
            </Slider>
            <div className={styles.mobileThumbnailNavigation}>
                <PrevArrow onClick={handlePreviousClick} disabled={!canGoToPrevious} isMobile />
                <ThumbnailStrip
                    images={images}
                    currentSlideIndex={currentSlideIndex}
                    onThumbnailImageClick={(index) => sliderRef.current?.slickGoTo(index)}
                    isMobileInline
                    moreProductsImages={
                        showMoreProducts
                            ? relatedProducts
                                  .slice(0, 4)
                                  .flatMap((p) => (p.image_url ? [p.image_url] : []))
                            : undefined
                    }
                />
                <NextArrow onClick={handleNextClick} disabled={!canGoToNext} isMobile />
            </div>
            <div className={styles.desktopThumbnailNavigation}>
                <ThumbnailStrip
                    images={images}
                    currentSlideIndex={currentSlideIndex}
                    onThumbnailImageClick={(index) => sliderRef.current?.slickGoTo(index)}
                />
            </div>
        </div>,
        document.body
    );
}
